import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Warning, ArrowsClockwise, Lightning, ArrowRight } from "@phosphor-icons/react";
import { billingApi } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", txn: null, attempts: 0, error: null });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session_id" });
      return;
    }
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const txn = await billingApi.checkoutStatus(sessionId);
        if (txn.payment_status === "paid") {
          setState({ status: "success", txn, attempts: attempt });
          return;
        }
        if (txn.status === "expired") {
          setState({ status: "expired", txn, attempts: attempt });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ status: "timeout", txn, attempts: attempt });
          return;
        }
        timerRef.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", error: e.response?.data?.detail || "Status check failed" });
      }
    };

    poll(1);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" data-testid="billing-success-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#ff8c00]">
            <Lightning size={28} weight="fill" />
            <span className="font-heading font-black text-2xl tracking-tight">LOAD LOGIC</span>
          </Link>
        </div>

        <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-8 text-center">
          {state.status === "polling" && (
            <>
              <ArrowsClockwise size={56} className="text-[#4682b4] animate-spin mx-auto mb-4" />
              <h1 className="font-heading font-black text-2xl tracking-tight mb-2">Confirming payment…</h1>
              <p className="text-sm text-[#a1a1aa] mb-2">Hold tight — Stripe is letting us know.</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Attempt {state.attempts || 1} / {MAX_ATTEMPTS}</p>
            </>
          )}

          {state.status === "success" && (
            <>
              <CheckCircle size={56} weight="fill" className="text-[#22c55e] mx-auto mb-4" />
              <h1 className="font-heading font-black text-2xl tracking-tight mb-2">You're in. 🚀</h1>
              <p className="text-sm text-[#a1a1aa] mb-1">
                Payment of <strong className="text-white">${(state.txn?.amount_total || 0).toFixed(2)} {(state.txn?.currency || 'usd').toUpperCase()}</strong> confirmed.
              </p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#71717a] mb-6">
                Plan: {state.txn?.plan_id} · 30-day access granted
              </p>
              <Link
                to="/live"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.25em] font-black"
                data-testid="success-continue"
              >
                Open dispatch dashboard <ArrowRight size={12} weight="bold" />
              </Link>
            </>
          )}

          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <>
              <Warning size={56} weight="fill" className="text-[#ef4444] mx-auto mb-4" />
              <h1 className="font-heading font-black text-2xl tracking-tight mb-2">
                {state.status === "expired" ? "Session expired" : state.status === "timeout" ? "Still waiting…" : "Something went sideways"}
              </h1>
              <p className="text-sm text-[#a1a1aa] mb-6">
                {state.status === "timeout"
                  ? "Stripe hasn't confirmed yet. If you completed the payment, refresh this page in a minute — we'll catch up via webhook."
                  : state.error || "Try the checkout again from the pricing page."}
              </p>
              <div className="flex items-center justify-center gap-2">
                <Link to="/pricing" className="px-4 py-2 border border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00] hover:text-black text-[10px] uppercase tracking-[0.25em] font-black">
                  Back to pricing
                </Link>
                <Link to="/live" className="px-4 py-2 border border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-black">
                  Skip to dashboard
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
